/**
 * React Hook for managing bundled content initialization
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { BundledContentInitializer } from '../utils/platformBundleLoaders';

export interface BundledContentStatus {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  lastLoadedAt: string | null;
  version: string | null;
}

/**
 * Hook to initialize bundled content on app startup
 */
export const useBundledContent = (
  storage: any,
  useContentStore: any,
  autoInitialize: boolean = true
) => {
  const [status, setStatus] = useState<BundledContentStatus>({
    isLoaded: false,
    isLoading: false,
    error: null,
    lastLoadedAt: null,
    version: null
  });

  const initializationRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);

  const initializeBundledContent = useCallback(async (force: boolean = false) => {
    if (!storage || !useContentStore) {
      setStatus(prev => ({
        ...prev,
        error: 'Storage or content store not available'
      }));
      return;
    }

    // Prevent multiple simultaneous initializations
    if (initializationRef.current && !force) {
      return;
    }

    // Skip if already successfully initialized and not forced
    if (hasInitializedRef.current && !force) {
      return;
    }

    initializationRef.current = true;
    setStatus(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const initializer = new BundledContentInitializer(storage, useContentStore);
      
      // Check if initialization is needed (unless forced)
      const shouldInit = force || await initializer.shouldInitialize();
      
      if (shouldInit) {
        console.log('🚀 Initializing bundled content...');
        await initializer.initialize();
        
        // Update status
        const lastLoaded = await storage.getItem('bundled_content_loaded');
        const version = await storage.getItem('bundled_content_version');
        
        setStatus({
          isLoaded: true,
          isLoading: false,
          error: null,
          lastLoadedAt: lastLoaded,
          version
        });
        
        console.log('🚀 Bundled content initialization complete');
        hasInitializedRef.current = true;
      } else {
        // Version matches — check whether the in-memory ContentStore is populated.
        // On cold start there is a race: useBundledContent's fast path (just two
        // AsyncStorage key reads) can set isLoaded:true before the Zustand
        // ContentStore finishes its own async rehydration from `child-nav-content`.
        // If useMilestoneSync fires at that moment the ContentStore is empty and
        // AdminSegment 11255 / 11272 are not found, producing LogBox error overlays.
        // Forcing initialize() here is safe — addOrUpdateContent is idempotent.
        const storeState = useContentStore?.getState ? useContentStore.getState() : null;
        const hasRecords = (storeState?.records?.length ?? 0) > 0;

        if (!hasRecords) {
          console.log('🚀 ContentStore empty despite version match — re-running initialize to populate store (cold-start race condition)');
          await initializer.initialize();
        }

        // Update status
        const lastLoaded = await storage.getItem('bundled_content_loaded');
        const version = await storage.getItem('bundled_content_version');
        
        setStatus({
          isLoaded: true,
          isLoading: false,
          error: null,
          lastLoadedAt: lastLoaded,
          version
        });
        
        console.log('🚀 Bundled content already loaded');
        hasInitializedRef.current = true;
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('🚀 Bundled content initialization failed:', error);
      
      setStatus({
        isLoaded: false,
        isLoading: false,
        error: errorMessage,
        lastLoadedAt: null,
        version: null
      });
    } finally {
      initializationRef.current = false;
    }
  }, [storage, useContentStore]);

  // Auto-initialize on mount if enabled - only once
  useEffect(() => {
    if (autoInitialize && storage && useContentStore && !initializationRef.current && !hasInitializedRef.current) {
      initializeBundledContent();
    }
  }, [autoInitialize, initializeBundledContent]); // Remove storage and useContentStore from deps since they're in the callback deps

  return {
    status,
    initializeBundledContent,
    reloadBundledContent: () => initializeBundledContent(true)
  };
};